import {
  ConfirmationDialog,
  dateTime,
  DetailColumns,
  DetailPane,
  FormDialog,
  useDialogs,
  useErrors
} from '@management-ui/core';
import {Delete, Edit, RestoreFromTrash} from '@mui/icons-material';
import {reverse} from 'named-urls';
import * as React from 'react';
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useRouteMatch} from 'react-router';
import {useHistory} from 'react-router-dom';
import {ServiceContext} from '../../../../components/Services';
import routes from '../../../../routes';
import MaterialForm from '../../forms/MaterialForm';
import Tabs from './Tabs';
import Wrapper from './Wrapper';

const Material = ({archive = false}) => {
  const services = useContext(ServiceContext);
  const history = useHistory();
  const match = useRouteMatch();

  const {errors, toggleError} = useErrors(
    useMemo(() => ({material: 'Sorry the material could not be accessed'}), [])
  );

  const {openDialogs, toggleDialog} = useDialogs(['material', 'delete', 'restore']);

  const idRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [material, setMaterial] = useState(null);

  const loadMaterial = useCallback((id) => {
    if (idRef.current !== `${id}`) {
      idRef.current = `${id}`;
      setLoading(true);
      services.material.getMaterial(id, archive).then(retrieved => {
        setMaterial(retrieved);
        setLoading(false);
      }).catch(() => {
        toggleError('material', true);
        setLoading(false);
        setTimeout(() => {
          history.push(archive ? reverse(routes.archive.index, {tab: 'materials'}) : routes.materials.index);
        }, 3000);
      });
    }
  }, [archive, services, history, toggleError]);

  useEffect(() => {
    if (match.params?.id) {
      loadMaterial(match.params?.id);
    }
  }, [history, match, material, loadMaterial]);

  const [title, setTitle] = useState('Loading');
  const [crumbs, setCrumbs] = useState([...(archive ? Wrapper.archiveCrumbs : Wrapper.standardCrumbs)]);

  useEffect(() => {
    let header = 'Loading';
    let trail = [];
    if (material) {
      header = material.name;
      trail = [
        ...(material.archived ? Wrapper.archiveCrumbs : Wrapper.standardCrumbs),
        {title: header, link: ''}
      ];
    }
    setTitle(header);
    setCrumbs(trail);
  }, [material]);

  const handleSaved = useCallback((updated) => {
    toggleDialog('material', false);
    if (updated) {
      setMaterial(updated);
    } else if (material?.id) {
      loadMaterial(material.id);
    }
  }, [toggleDialog, setMaterial, loadMaterial, material]);

  const handleDelete = useCallback((confirmed) => {
    toggleDialog('delete', false);
    if (confirmed) {
      setLoading(true);
      services.material.deleteMaterial(material).then(() => {
        setLoading(false);
        history.push(routes.materials.index);
      }).catch(() => setLoading(false));
    }
  }, [services, history, toggleDialog, material]);

  const handleRestore = useCallback((confirmed) => {
    toggleDialog('restore', false);
    if (confirmed) {
      setLoading(true);
      services.material.restoreMaterial(material).then((restored) => {
        setLoading(false);
        history.push(reverse(routes.materials.detail, {id: material.id}));
        setMaterial(restored);
      }).catch(() => setLoading(false));
    }
  }, [services, history, toggleDialog, material]);

  return (
    <Wrapper title={title} material={material} loading={loading} crumbs={crumbs} errors={errors}>
      {material?.id ? (
        <>
          <DetailColumns columns={[

            <DetailPane
              title="Material Details"
              actions={material.archived ? [
                {title: 'Restore', icon: <RestoreFromTrash/>, onClick: () => toggleDialog('restore', true)},
              ] : [
                {title: 'Edit', icon: <Edit/>, onClick: () => toggleDialog('material', true)},
                {title: 'Delete', icon: <Delete/>, onClick: () => toggleDialog('delete', true)},
              ]}
              details={[
                {title: 'Name', value: material.name}
              ]}
              dialogs={[
                (props) => (
                  <FormDialog
                    {...props}
                    title="Edit"
                    open={openDialogs['material'] ?? false}
                    onClose={() => toggleDialog('material', false)}
                    maxWidth="sm"
                    render={(props) => (
                      <MaterialForm
                        {...props}
                        material={material}
                        onSaved={handleSaved}/>
                    )}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['delete'] ?? false}
                    title="Delete Material"
                    message="Are you sure you want to delete this material?"
                    onClose={handleDelete}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['restore'] ?? false}
                    title="Restore Material"
                    message="Are you sure you want to restore this material?"
                    onClose={handleRestore}
                  />
                )
              ]}
            />,

            <DetailPane
              title="Admin Details"
              details={[
                {title: 'Created', value: dateTime(material.created_at)},
                {title: 'Last Updated', value: dateTime(material.updated_at)}
              ]}
            />,
          ]}/>

          <Tabs material={material}/>
        </>
      ) : null}
    </Wrapper>
  );
};

export default Material;
